import api from "../../services/api";
import type { TournamentMatch } from "../matches/types";
import type {
  TournamentClub,
  CreateTournamentClubRequest,
  UpdateTournamentClubRequest,
  GetTournamentClubsResponse,
} from "./types";

export const clubsApi = {
  /**
   * Create a new tournament club/team
   * Supports both JSON and FormData (for logo upload)
   */
  createTournamentClub: async (data: CreateTournamentClubRequest | FormData): Promise<TournamentClub> => {
    return api.post("/tournament-clubs", data);
  },

  /**
   * Get all tournament clubs/teams (paginated)
   */
  getTournamentClubs: async (params?: { page?: number; limit?: number }): Promise<GetTournamentClubsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) {
      queryParams.append("page", params.page.toString());
    }
    if (params?.limit) {
      queryParams.append("limit", params.limit.toString());
    }
    const queryString = queryParams.toString();
    const url = queryString ? `/tournament-clubs?${queryString}` : "/tournament-clubs";
    return api.get(url);
  },

  /**
   * Get clubs/teams by tournament (paginated)
   * This is the main endpoint to get the teams list for a tournament
   */
  getClubsByTournament: async (
    tournamentId: string,
    params?: {
      page?: number;
      limit?: number;
      search?: string;
      minPlayers?: number;
      maxPlayers?: number;
      createdAtFrom?: string;
      createdAtTo?: string;
      sortBy?: "name" | "captain" | "playerCount" | "createdAt";
      direction?: "asc" | "desc";
    }
  ): Promise<GetTournamentClubsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) {
      queryParams.append("page", params.page.toString());
    }
    if (params?.limit) {
      queryParams.append("limit", params.limit.toString());
    }
    if (params?.sortBy) {
      queryParams.append("sortBy", params.sortBy);
    }
    if (params?.direction) {
      queryParams.append("direction", params.direction);
    }
    if (params?.search) {
      queryParams.append("search", params.search);
    }
    if (params?.minPlayers !== undefined) {
      queryParams.append("minPlayers", params.minPlayers.toString());
    }
    if (params?.maxPlayers !== undefined) {
      queryParams.append("maxPlayers", params.maxPlayers.toString());
    }
    if (params?.createdAtFrom) {
      queryParams.append("createdAtFrom", params.createdAtFrom);
    }
    if (params?.createdAtTo) {
      queryParams.append("createdAtTo", params.createdAtTo);
    }
    const queryString = queryParams.toString();
    const url = queryString
      ? `/tournament-clubs/tournament/${tournamentId}?${queryString}`
      : `/tournament-clubs/tournament/${tournamentId}`;
    return api.get(url);
  },

  /**
   * Get tournament club/team by ID
   */
  getTournamentClub: async (id: string): Promise<TournamentClub> => {
    return api.get(`/tournament-clubs/${id}`);
  },

  /**
   * Get tournament club/team detail with players
   */
  getTournamentClubDetail: async (
    id: string
  ): Promise<{
    team: TournamentClub & {
      teamCaptainName?: string;
      playerCount?: number;
    };
    players: Array<{
      id: string;
      tournamentId?: string;
      userId?: string;
      email?: string;
      clubId?: string;
      clubName?: string;
      isCaptain?: boolean;
      isManual?: boolean;
      name?: string;
      gender?: "male" | "female" | "other";
      position?: string;
      number?: number;
      dateOfBirth?: string;
      coverPhoto?: string;
      createdAt: string;
    }>;
    matches?: TournamentMatch[];
  }> => {
    return api.get(`/tournament-clubs/${id}`);
  },

  /**
   * Update tournament club/team
   */
  updateTournamentClub: async (id: string, data: UpdateTournamentClubRequest): Promise<TournamentClub> => {
    return api.put(`/tournament-clubs/${id}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  /**
   * Delete tournament club/team
   */
  deleteTournamentClub: async (id: string): Promise<void> => {
    return api.delete(`/tournament-clubs/${id}`);
  },

  /**
   * Assign a player as team captain
   */
  assignCaptain: async (id: string, playerId: string): Promise<TournamentClub> => {
    return api.post(`/tournament-clubs/${id}/assign-captain`, { playerId });
  },
};
